import React, { useState, useEffect } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../../components/LoadingSpinner'
import { api } from '../../services/api'
import {
  CalendarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  CogIcon,
  UserCircleIcon,
  TruckIcon,
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

interface DashboardStats {
  totalBookings: number
  activeBookings: number
  completedBookings: number
  totalReviews: number
  averageRating: number
}

interface Provider {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  isVerified: boolean
  isApproved: boolean
  serviceProvider?: {
    id: string
    companyName: string
    serviceType: string
    rating: number
  }
}

interface DashboardData {
  provider: Provider
  stats: DashboardStats
  recentBookings: any[]
}

const ProviderDashboardPage: React.FC = () => {
  const { user } = useAuth()
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Redirect VENDOR role to souvenir vendor dashboard
  if (user?.serviceProvider?.serviceType === 'ISLAMIC_SOUVENIRS' || user?.role === 'VENDOR') {
    return <Navigate to="/vendor-dashboard" replace />
  }

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      // Fetch dashboard stats and recent bookings separately
      const [statsResponse, recentBookingsResponse] = await Promise.all([
        api.get('/provider-dashboard/dashboard/stats'),
        api.get('/provider-dashboard/dashboard/recent-bookings?limit=5')
      ])

      // Combine the responses
      const data = {
        provider: {
          ...statsResponse.data.provider,
          email: user?.email || '',
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          role: user?.role || '',
        },
        stats: statsResponse.data.stats,
        recentBookings: recentBookingsResponse.data
      }

      setDashboardData(data)
    } catch (err: any) {
      console.error('Dashboard fetch error:', err)
      setError(err.response?.data?.message || err.message || 'Failed to fetch dashboard data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !dashboardData) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Error loading dashboard: {error}
      </div>
    )
  }

  const statsCards = [
    {
      name: 'Total Bookings',
      value: dashboardData.stats.totalBookings,
      icon: CalendarIcon,
      color: 'bg-blue-500'
    },
    {
      name: 'Active Bookings',
      value: dashboardData.stats.activeBookings,
      icon: ClockIcon,
      color: 'bg-yellow-500'
    },
    {
      name: 'Completed Bookings',
      value: dashboardData.stats.completedBookings,
      icon: UserGroupIcon,
      color: 'bg-green-500'
    },
    {
      name: 'Average Rating',
      value: `${dashboardData.stats.averageRating.toFixed(1)} ★`,
      icon: CurrencyDollarIcon,
      color: 'bg-purple-500'
    }
  ]

  const navigationItems = [
    {
      name: 'Profile',
      href: '/provider-dashboard/profile',
      icon: UserCircleIcon,
      description: 'Manage your provider profile'
    },
    {
      name: 'Assets',
      href: '/provider-dashboard/assets',
      icon: TruckIcon,
      description: 'Manage your vehicles and equipment'
    },
    {
      name: 'Bookings',
      href: '/provider-dashboard/bookings',
      icon: CalendarIcon,
      description: 'View and manage your bookings'
    },
    {
      name: 'Analytics',
      href: '/provider-dashboard/analytics',
      icon: ChartBarIcon,
      description: 'View performance analytics'
    },
    {
      name: 'Schedule',
      href: '/provider-dashboard/schedule',
      icon: ClockIcon,
      description: 'Manage your availability'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {dashboardData.provider.firstName}!
            </h1>
            <p className="text-gray-600 mt-1">
              {dashboardData.provider.serviceProvider?.companyName || 'Your Service Provider Dashboard'}
            </p>
            <div className="flex items-center mt-2 space-x-4">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                dashboardData.provider.isApproved
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {dashboardData.provider.isApproved ? 'Approved' : 'Pending Approval'}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                dashboardData.provider.isVerified
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {dashboardData.provider.isVerified ? 'Verified' : 'Not Verified'}
              </span>
              {dashboardData.provider.serviceProvider?.rating && (
                <span className="inline-flex items-center">
                  <span className="text-yellow-400 mr-1">★</span>
                  <span className="text-sm text-gray-600">
                    {dashboardData.provider.serviceProvider.rating.toFixed(1)}
                  </span>
                </span>
              )}
            </div>
          </div>
          <div className="flex space-x-3">
            <Link
              to="/provider-dashboard/profile"
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Edit Profile
            </Link>
            <Link
              to="/provider-dashboard/assets/new"
              className="bg-secondary-600 hover:bg-secondary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Add Asset
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className={`flex-shrink-0 p-3 rounded-md ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.name}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stat.value}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions & Navigation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Navigation */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                >
                  <item.icon className="h-6 w-6 text-primary-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Bookings</h3>
          </div>
          <div className="p-6">
            {dashboardData.recentBookings.length > 0 ? (
              <div className="space-y-4">
                {dashboardData.recentBookings.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {booking.funeralCase?.deceasedName || 'Booking'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(booking.scheduledDateTime).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      booking.status === 'COMPLETED'
                        ? 'bg-green-100 text-green-800'
                        : booking.status === 'CONFIRMED'
                        ? 'bg-blue-100 text-blue-800'
                        : booking.status === 'IN_PROGRESS'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent bookings</p>
            )}
            <div className="mt-4">
              <Link
                to="/provider-dashboard/bookings"
                className="block w-full text-center bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                View All Bookings
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProviderDashboardPage